import { tokens } from '@env0/ui-theme';
import { getAntdTheme as getGeneratedAntdTheme } from '@env0/ui-theme/antd';
import type { ThemeConfig } from 'antd';
import merge from 'lodash/merge';
import { PRIMARY_FONT_FAMILY, type Breakpoints, type Env0Theme } from './themes.constants';

/**
 * Every other antd mapping is generated from Figma's `Bridge - AntD` collection. This one is
 * hand-written, and stays that way: which key each component reads is a function of the antd version,
 * not a design decision, so completing the collection in Figma would generate the wrong thing.
 *
 * `ANTD_CODE_OWNED_SEED` in `style-dictionary/formatters/bridges.ts` drops both keys from the bridge,
 * so a Figma push cannot land its own seed values here. Per-component `zIndexPopup` is not protected:
 * the bridge is the only source for Message and Select.
 */
const zIndexTokens = {
  zIndexBase: tokens.zIndex.base,
  zIndexPopupBase: tokens.zIndex.dropdown
};

/**
 * Alert's `ComponentToken` is empty in antd 5.6.4, so these twelve alias tokens are its entire visual
 * restyle and no call site changes to get it.
 *
 * Scoped under `components.Alert`, never on the global token: `colorErrorBg` is also read by
 * `button/style`, `menu/style` and `upload/style/picture`, and it and `colorWarningBg` derive
 * `colorErrorOutline` / `colorWarningOutline` in `theme/util/alias.js`. `ANTD_CODE_OWNED_SEED` in
 * `style-dictionary/formatters/bridges.ts` keeps all twelve off the bridge's seed for that reason.
 *
 * `colorText` is deliberately absent - `text.<status>` on `surface.<status>.background` fails AA in
 * light for success, warning and error, so status tokens colour the icon and border only.
 *
 * Notification (APO-550) spreads the same set, so the contrast decision is made once; antd 5.6.4's
 * notification style reads only the four foreground keys, ignoring the Bg and Border ones.
 */
export const statusTokens = (mode: Env0Theme['mode']) => {
  const { surface, border, icon } = tokens.theme[mode];

  return {
    colorInfoBg: surface.information.background,
    colorInfoBorder: border.info,
    colorInfo: icon.information,

    colorSuccessBg: surface.success.background,
    colorSuccessBorder: border.success,
    colorSuccess: icon.success,

    colorWarningBg: surface.warning.background,
    colorWarningBorder: border.warning,
    // `icon.warning` is 2.24:1 on this background in light, under the 3:1 non-text floor;
    // `action-hover` is 4.65:1. Dark clears it at 8.76:1. Token gap raised with design.
    colorWarning: mode === 'light' ? surface.warning['action-hover'] : icon.warning,

    colorErrorBg: surface.error.background,
    colorErrorBorder: border.error,
    colorError: icon.error
  };
};

/**
 * Modal has no `zIndexPopup` in its ComponentToken and reads the `zIndexPopupBase` seed directly. The
 * rest read `zIndexPopup`, the value their style consumes; seeding those instead renders the DS number
 * plus antd's per-component offset (popover +30, notification +50, tooltip +70), so no DS value would
 * reach the DOM.
 *
 * The ladder ranks dropdown below drawer and modal, which holds only because AntdWrapper portals a
 * nested popup into its container: 5.6.4 has no `zIndexContext`, so otherwise every popup lands on
 * document.body and a Select inside a Popover renders behind it.
 */
const componentOverrides = ({ mode }: Env0Theme) => ({
  Modal: { zIndexPopupBase: tokens.zIndex.modal },
  Drawer: {
    zIndexPopup: tokens.zIndex.drawer,
    // Equal to antd 5.6.4's default today; pinned so the footer follows the DS ladder, not antd's.
    footerPaddingBlock: toPx(tokens.spacing.padding.xs),
    footerPaddingInline: toPx(tokens.spacing.padding.lg)
  },
  Popover: { zIndexPopup: tokens.zIndex.popover },
  Notification: {
    zIndexPopup: tokens.zIndex.toast,
    // Without this antd's own dark surface is 1.11:1 against the page and its shadow is black on
    // black, leaving the toast with no visible edge. Same value the bridge gives Drawer.
    colorBgElevated: tokens.theme[mode].surface.main['container-on-card'],
    ...statusIconTokens(mode)
  },
  /**
   * The generated bridge maps surface/main/cards onto dark's foreground here, giving 1.33:1 on the
   * spotlight. Deleting the block does not fix it either - the light global is 1.43:1 on the same
   * background. One value for both modes because the spotlight itself is one colour in both; a
   * primitive because no semantic token means "foreground over the spotlight". Raised with design so
   * the next `Bridge - AntD` rebuild does not reintroduce it.
   */
  Tooltip: {
    zIndexPopup: tokens.zIndex.tooltip,
    colorTextLightSolid: tokens.colors.neutral.white
  }
});

const toPx = (value: string) => parseInt(value, 10);

/**
 * Neither Result nor Skeleton is in the generated bridge and Figma specs neither — extrapolated from
 * the ramp per D1. Takes the mode because the Skeleton pair is colour: fixed light greys would give a
 * near-white shimmer on dark surfaces, so dark sweeps from the dark end of the same scale (700 → 650).
 */
const feedbackComponents = (mode: 'light' | 'dark') => ({
  Result: {
    // h3, not h4: a Result is a full-page outcome.
    titleFontSize: toPx(tokens.typography.fontSize.heading.h3),
    subtitleFontSize: toPx(tokens.typography.fontSize.body.md),
    iconFontSize: toPx(tokens.spacing.scale['1000'])
  },
  Skeleton: {
    gradientFromColor: mode === 'dark' ? tokens.colors.neutral['700'] : tokens.colors.neutral['300'],
    gradientToColor: mode === 'dark' ? tokens.colors.neutral['650'] : tokens.colors.neutral['200'],
    blockRadius: toPx(tokens.spacing.radius['100'])
  }
});

/**
 * Avatar and Badge colours are not in either ComponentToken: 5.6.4 gives Avatar geometry plus
 * `groupBorderColor`, and Badge declares no ComponentToken at all. Both are still themeable, but only
 * through the alias tokens their styles read:
 *
 * - `avatar/style` (index.js:111) takes `colorTextPlaceholder` for the circle and `colorTextLightSolid`
 *   for the initials; `groupBorderColor` is the ring between stacked avatars.
 * - `badge/style` takes `colorError` for the dot's fill (index.js:355) and `colorBorderBg` for the ring
 *   that cuts it out of the surface beneath (index.js:371). `colorBgContainer` is a third thing - the
 *   count's own text - and is deliberately not set here.
 *
 * Figma specifies all of this in `Surface.Avatar.*` and `Text.avatar`, but bound it to the bridge under
 * invented antd key names; `ANTD_CODE_OWNED_COMPONENTS` drops those, and the intent is mapped here.
 *
 * `Surface.Avatar.icon` has no antd hook and is deliberately unmapped: the icon variant inherits the
 * initials' colour, so colouring it separately would take a CSS rule. Raised with design.
 */
const avatarAndBadgeComponents = (mode: Mode) => {
  const { surface, text, icon } = tokens.theme[mode];

  return {
    Avatar: {
      colorTextPlaceholder: surface.avatar['background-default'],
      colorTextLightSolid: text.avatar,
      groupBorderColor: surface.avatar.border
    },
    Badge: {
      colorError: icon.error,
      // The ring that cuts the dot out of whatever sits beneath it, which is what Figma binds to the
      // Ellipse's stroke. `badge/style` reads it as `badgeShadowColor: colorBorderBg` (index.js:371),
      // not `colorBgContainer` - that one is the count's own text.
      colorBorderBg: surface.main.cards
    }
  };
};

/** The icon half of `statusTokens`: a toast has no tinted fill or border to colour. */
const statusIconTokens = (mode: Env0Theme['mode']) => {
  const { colorSuccess, colorInfo, colorWarning, colorError } = statusTokens(mode);

  return { colorSuccess, colorInfo, colorWarning, colorError };
};

/**
 * Hand-written for the same reason as the z-index mappings: which alias token antd reads is a fact
 * about the version, not a design decision. Button's ComponentToken is empty in 5.6.4, so only the
 * alias tokens scoped here take effect - each was measured reaching the emitted CSS. Real values
 * rather than `var()`: antd derives palettes from what it is given.
 *
 * Mechanics worth knowing before editing:
 * - Radius and label size are read per size variant, so setting only the base keys leaves large and
 *   small on antd's own ladder.
 * - Horizontal padding is `buttonPaddingHorizontal - lineWidth`; the border adds the pixel back, so
 *   the token value is the visual inset. Small's is hardcoded to 8 in antd and is not configurable.
 * - Secondary is antd's `default` type, and Figma binds it no surface variable at all - a border and
 *   a label over nothing, hence `transparent`.
 */
const buttonComponent = (mode: 'light' | 'dark') => {
  const { surface, text, border } = tokens.theme[mode];
  const px = (value: string) => parseInt(value, 10);

  return {
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 28,
    borderRadius: px(tokens.spacing.radius['100']),
    borderRadiusLG: px(tokens.spacing.radius['100']),
    borderRadiusSM: px(tokens.spacing.radius['100']),
    lineWidth: px(tokens.spacing.borderWidth.xs),
    fontSize: 14,
    fontSizeLG: 14,
    paddingContentHorizontal: px(tokens.spacing.padding.lg),

    colorPrimary: surface.primary.action,
    colorPrimaryHover: surface.primary['action-hover'],
    colorPrimaryActive: surface.primary['action-hover'],
    colorTextLightSolid: text.onAction,

    colorBgContainer: 'transparent',
    // border.primary (neutral grey), not border.secondary (the lime brand edge): the Figma Secondary
    // draws a neutral grey border (node 34:1041), and this is antd's own default colorBorder too.
    colorBorder: border.primary,
    // `text.heading`, not the `text.onAction` Figma binds: that token means "over a lime fill" and is
    // black in both modes, so on an unfilled Secondary it vanished in dark. Same value in light.
    colorText: text.heading,

    colorBgContainerDisabled: surface.disabled.action,
    // Both modes diverge from Figma's text.onDisabled for the same reason: in dark it equals the fill
    // (#4d4d4d on #4d4d4d), and in light it is #e6e6e6 on #edf0f3 — 1.09:1, an invisible label on the
    // Discovery "Import" button (UI-polish review, item J). text.disabled reads in both.
    colorTextDisabled: text.disabled
  };
};

type Mode = Env0Theme['mode'];

/**
 * Hand-written for the same reason as the z-index mappings: which token antd reads is a fact about 5.6.4,
 * not a design decision. Every key here was measured reaching the emitted CSS; absent keys were measured
 * not reaching it.
 *
 * Switch sets no geometry on purpose. 5.6.4 has no Switch ComponentToken and derives track, handle and
 * min-width from `fontSize`, `lineHeight` and `controlHeight` — values that already equal the Figma
 * variables. Those variables are named after a later antd's ComponentToken keys, which do not exist here.
 */
const switchComponent = (mode: Mode) => {
  const { surface } = tokens.theme[mode];

  return {
    colorPrimary: surface.switch['track-active'],
    colorPrimaryHover: surface.switch['track-active'],
    colorTextQuaternary: surface.switch['track-inactive'],
    colorTextTertiary: surface.switch['track-inactive'],
    // The handle, despite antd deriving `switchBg` from `colorBgContainer` — that key does not reach at
    // component scope, and without this the dark handle stays white.
    colorWhite: surface.switch.handle
  };
};

/**
 * Deliberately minimal, and defensive rather than load-bearing: every former raw-antd call site
 * renders the common wrapper, so this block only has to keep a stray antd Card from looking wrong. The
 * card's real styling lives in `components/common/card.tsx`.
 *
 * Card's own `ComponentToken` in 5.6.4 is header/actions geometry only, so everything below except
 * `headerBg` / `headerFontSize` is an alias token scoped to the component. Which ones reach was probed,
 * not assumed: `colorBorderSecondary` paints every Card border and `colorBorder` never reaches at all,
 * `paddingLG` drives both head and body, and `borderRadiusLG` drives the card plus its head/body/cover
 * corners. `fontSizeLG` reaches only `.ant-card-meta-title`, so it is not set here.
 *
 * `headerBg: transparent` because the wrapper draws the header rule itself - antd's filled head would
 * double it.
 */
const cardComponent = (mode: Mode) => {
  const { surface, text, border } = tokens.theme[mode];

  return {
    // Measured off all 19 billing-card frames on the Page template page: radius 12, 1px inside stroke.
    borderRadiusLG: toPx(tokens.spacing.radius['400']),
    lineWidth: toPx(tokens.spacing.borderWidth.xs),
    paddingLG: toPx(tokens.spacing.padding.xxl),
    colorBgContainer: surface.main.cards,
    colorBorderSecondary: border.primary,
    colorTextHeading: text.heading,
    headerBg: 'transparent',
    headerFontSize: toPx(tokens.typography.fontSize.heading.h4)
  };
};

/**
 * Completes the generated bridge's Collapse block, which carries the two backgrounds, the header colour
 * and the header padding but nothing for the divider or the body text.
 *
 * Collapse's `ComponentToken` is empty in 5.6.4, so all of it is alias tokens; a sentinel probe found
 * all sixteen candidates reach. Only the two that change a rendered value are set — every collapse in
 * the app renders `bordered={false}` (see `collapse-components/collapse.tsx`), and the borderless style
 * keeps just one visible rule from the geometry set: the `lineWidth lineType colorBorder` divider
 * between items. `lineWidth` already resolves to 1, and the radius only reaches corners the borderless
 * style zeroes, so neither is set.
 */
const collapseComponent = (mode: Mode) => {
  const { text, border } = tokens.theme[mode];

  return {
    colorBorder: border.primary,
    // `.ant-collapse-content`'s inherited colour. The generated block sets `colorTextHeading` for the
    // header but leaves the body on antd's default, which is the heading colour.
    colorText: text.body
  };
};

const checkboxComponent = (mode: Mode) => {
  const { surface, text, icon } = tokens.theme[mode];

  return {
    // Not `checkboxSize`: `getStyle` re-derives that from this key through `mergeToken`, discarding it.
    controlInteractiveSize: 16,
    borderRadiusSM: parseInt(tokens.spacing.radius['100'], 10),
    lineWidth: parseInt(tokens.spacing.borderWidth.xs, 10),

    // antd paints the checked border from `colorPrimary` too, so Figma's separate `border.action` cannot
    // be honoured — identical in light, `#d2f740` rather than `#ddf970` in dark.
    colorPrimary: surface.primary.action,
    colorPrimaryHover: surface.primary['action-hover'],
    // The checkmark. antd draws it with `colorWhite`, which would be white on lime.
    colorWhite: text.onAction,
    colorBorder: icon.secondary,
    // The disabled tick: text.disabled was 2.2:1 on the disabled fill, so checked and unchecked
    // disabled boxes read as the same grey square (UI-polish review, item J). antd draws the tick
    // as a border, and antd's own disabled border stays on colorBorder, distinct from the fill.
    colorTextDisabled: text.body,
    colorBgContainerDisabled: icon.disabled
  };
};

/**
 * Radio has no Figma component, so per D1 it extrapolates from checkbox. Colours too: the bridge binds
 * its colorPrimary to `Surface.Success.action-hover`, a status ramp, which put a dark-green radio
 * beside a lime checkbox in the same form (items F and I). Corrected here until Figma is repointed at
 * `Surface.Primary.*` and regenerated.
 */
const radioComponent = (mode: Mode) => {
  const { surface, text, icon } = tokens.theme[mode];

  return {
    radioSize: 16,
    wrapperMarginInlineEnd: parseInt(tokens.spacing.spacing.xxs, 10),
    colorPrimary: surface.primary.action,
    colorPrimaryHover: surface.primary['action-hover'],
    colorPrimaryActive: surface.primary['action-hover'],
    // The checked dot; antd draws it with `colorWhite`, which would be white on lime.
    colorWhite: text.onAction,
    dotColorDisabled: icon.disabled
  };
};

/**
 * The `--component-field-*` group is the design's own form-field token set. Resolved from
 * `tokens.theme[mode]` rather than `tokens.components.field`, which is light-only; antd derives
 * palettes at theme-build time and needs real per-mode values.
 *
 * Mechanics measured by rendering with sentinel values and reading the emitted sheet:
 * - `.ant-input` declares no height. Height is padding + line-height + border, and antd derives the
 *   vertical padding from `controlHeight` after this merge, clamped at 3px - so today's 28px renders
 *   30. 32 is exact, and `controlHeightSM` 28 is exact too (its clamp floor is 0).
 * - Horizontal inset is `paddingSM - lineWidth` plus the border, i.e. `paddingSM` is the visual inset.
 * - Hover and focus border are one declaration in 5.6.4; both tokens alias --border-focus-primary.
 * - Disabled reuses `colorBorder`, so --component-field-border-disabled cannot come from here.
 * - The addon strip reads `colorFillAlter`; 5.6.4 has no `addonBg`.
 * - The generated bridge sets Input.controlHeight 24, which renders a 30px field. Overridden here.
 */
const fieldComponent = (mode: 'light' | 'dark') => {
  const { surface, text, border, icon } = tokens.theme[mode];
  const px = (value: string) => parseInt(value, 10);
  const radius = px(tokens.spacing.radius['100']);

  return {
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 28,
    borderRadius: radius,
    borderRadiusLG: radius,
    borderRadiusSM: radius,
    lineWidth: px(tokens.spacing.borderWidth.xs),
    fontSize: 14,
    paddingSM: px(tokens.spacing.padding.md),
    // The affix icon-to-text gap. Not `inputAffixPadding`, which `initInputToken` assigns inside
    // `mergeToken` - after the ConfigProvider merge, so a value set on it is discarded.
    paddingXXS: px(tokens.spacing.padding.xxs),

    colorBgContainer: surface.main.cards,
    colorFillAlter: surface.main.cards,
    colorBorder: border.inputFieldDefault2,
    colorPrimaryHover: border.focusPrimary,
    colorText: text.heading,
    colorTextPlaceholder: text.secondary,
    colorTextDescription: icon.secondary,
    // The clear cross. A literal because there is no DS icon-size token group.
    fontSizeIcon: 16,
    colorTextQuaternary: icon.secondary,

    controlOutline: border.focusPrimary,
    controlOutlineWidth: px(tokens.spacing.borderWidth.sm),

    // Disabled keeps the resting fill: both background tokens alias surface/main/cards in both modes,
    // so border and text carry the state. The app-wide colorBgContainerDisabled would grey it.
    colorBgContainerDisabled: surface.main.cards,
    colorTextDisabled: text.disabled,
    colorError: border.error
  };
};

/**
 * One token, carrying a value antd cannot derive: Nav / Tab binds its active indicator to
 * `Surface/Primary/action - border` (152:16), not to the action fill `colorPrimary` derives — and in
 * dark `darkAlgorithm` would shift that derivation besides. Set in both modes; the light-only spec
 * passed with this deleted, and it took a dark browser pass to catch.
 *
 * What antd cannot do is give the label a different colour from the underline: `theme.js` draws both
 * from this one token. `components/common/menu.tsx` repaints the label on `.ant-menu-title-content`,
 * which antd gives only a transition, so it needs no `!important` and no doubled class.
 *
 * `Menu.itemSelectedBg` in the generated bridge is the lime — a nav-rail value. It reaches vertical
 * menus only, so it never touches the tab bar; `drift-analysis-menu.tsx` already neutralises it
 * locally, and the rail itself belongs to C2/C3. Raise it there rather than repainting them from here.
 */
const menuComponent = (mode: 'light' | 'dark') => ({
  horizontalItemSelectedColor: tokens.theme[mode].surface.primary['action-border']
});

/**
 * Table's `ComponentToken` is empty in 5.6.4, so these alias tokens are its entire visual restyle, each
 * measured reaching a Table selector with a probe. Three of antd's derivations are many-to-one and so
 * live in `common/table.tsx` instead: row hover, cell block padding, and the active pager label.
 */
const tableComponent = (mode: Mode) => {
  const { surface, text, border, icon } = tokens.theme[mode];

  return {
    padding: toPx(tokens.spacing.padding.lg),
    // radius-400, matching Card: tables stack directly under 12px cards and callouts, and two radii on
    // one page read as a defect (UI-polish review).
    borderRadiusLG: toPx(tokens.spacing.radius['400']),
    fontSize: 14,
    // Figma's header is Label Medium Regular; 500 renders Regular under the ABC Whyte collapse (400 -> Light).
    fontWeightStrong: 500,

    colorBgContainer: surface.main.cards,
    // Same value as colorBgContainer, which flattens header, footer, sorted column and expanded row.
    colorFillAlter: surface.main.cards,
    colorTextHeading: text.heading,
    colorText: text.body,
    // Figma paints no fill on any of the four header sort states, so the sort state is the icon alone.
    colorFillSecondary: surface.main.cards,
    // Sortable-header hover. Not a Figma state; extrapolated per D1 because a clickable header needs one.
    colorFillContent: surface.main['element-on-card'],
    colorBorderSecondary: border.primary,
    controlItemBgActive: surface.primary.background,
    controlItemBgActiveHover: surface.primary.background,

    colorIcon: icon.secondary,
    colorIconHover: text.heading,
    // Per mode, unlike the generated bridge's single #b3d049: the DS shifts icon.action to #ddf970 in
    // dark, and a light-only check cannot see it because antd's default derives the same lime (B10).
    colorPrimary: icon.action,
    // antd fades the resting sorter by opacityLoading, putting icon.secondary at ~2.1:1 in light - under
    // the 3:1 non-text floor.
    opacityLoading: 1
  };
};

/**
 * `pagination/style` draws the active item's border AND its link colour from one `colorPrimary`, but the
 * design wants a lime border with near-black text, so `common/table.tsx` repaints the label.
 */
const paginationComponent = (mode: Mode) => {
  const { surface, text, border, icon } = tokens.theme[mode];

  return {
    itemSize: 32,
    itemSizeSM: 24,
    itemBg: surface.main.cards,
    itemActiveBg: surface.main.cards,
    itemLinkBg: surface.main.cards,
    itemInputBg: surface.main.cards,
    itemActiveBgDisabled: surface.disabled.background,
    itemActiveColorDisabled: text.disabled,

    borderRadius: toPx(tokens.spacing.radius['100']),
    fontSize: 14,
    fontWeightStrong: 700,
    colorText: text.body,
    colorTextDisabled: text.disabled,
    colorBorder: border.primary,
    colorPrimary: icon.action,
    colorPrimaryHover: icon.action
  };
};

/**
 * Every horizontal tab surface renders through `components/common/tabs.tsx`, so this block is the
 * Nav / Tab palette. Only the keys that differ from antd 5.6.4's defaults are set: `inkBarColor` takes
 * `Surface/Primary/action - border` (152:16) rather than the action fill antd derives, the label tokens
 * would otherwise put a lime label on a lime ink bar, and the bridge's `cardBg` is a dark value on a
 * light card.
 */
const tabsComponent = (mode: 'light' | 'dark') => {
  const { surface, text } = tokens.theme[mode];

  return {
    inkBarColor: surface.primary['action-border'],
    itemHoverColor: text.heading,
    itemSelectedColor: text.heading,
    itemActiveColor: text.heading,
    cardBg: surface.main.cards
  };
};

/**
 * Measured with a sentinel probe, then narrowed by deleting each key and re-running (APO-462). Modal has
 * only six real ComponentToken keys in 5.6.4 - `contentBg`, `headerBg`, `footerBg`, `titleColor`,
 * `titleFontSize`, `titleLineHeight`. Everything else that looks like a token is recomputed by
 * `mergeToken(token, {...})` inside `genComponentStyleHook`, which runs AFTER this merge:
 * `modalBodyPadding`, `modalHeaderPadding`, `modalFooterPadding*`, `modalHeaderHeight` and
 * `modalCloseBtnSize` are all silently discarded.
 *
 * Only keys that change what antd renders are set here. Four Figma values are already what the app
 * renders without them, so setting them would be untestable noise - the emitted-CSS cases in
 * `antd.constants.spec.tsx` guard the rendered result instead, and will fail if any of these moves:
 * - content padding 20px 24px: antd 5.6.4's own `paddingMD` / `paddingContentHorizontalLG` defaults;
 * - the 4px close-button radius: derived from the generated bridge's global `borderRadius: 4`;
 * - the title colour: the generated bridge's global `colorTextHeading` already equals `text.heading` in
 *   both modes, and antd defaults `titleColor` to it.
 *
 * `borderRadiusLG` IS set, despite 8 being antd's stock default: that same global `borderRadius: 4`
 * drags antd's derived large radius off 8, so the modal needs it back.
 *
 * Absent on purpose:
 * - `marginXS`. antd reads it for the header's bottom margin AND the gap between footer buttons, and 8
 *   is exactly Figma's button gap - so the ~18px header gap belongs in `modal.tsx`, not here.
 * - `marginSM`, the footer's top margin. The generated bridge already ships 32; the Modal frame measures
 *   ~26. Figma disagrees with itself, so this keeps 32 rather than inventing a third number.
 * - `padding`. It renders nothing as a padding - its only effect is through `modalHeaderHeight`, which
 *   positions the close button. Leaving it on antd's value keeps that offset at 17px; `modal.tsx` sets
 *   the vertical, which Figma puts at 29.
 * - `footerBg`. antd defaults it to `transparent`, which shows `contentBg` through.
 *
 * `headerBg` IS set, and a light-only check would have missed why: antd defaults it to `colorBgElevated`,
 * which `darkAlgorithm` derives to #1f1f1f while `contentBg` is #151514 - a visibly lighter band across
 * the top of every dark modal. In light both land on #ffffff, so the bug is invisible there.
 */
const modalComponent = (mode: Mode) => {
  const { surface, icon } = tokens.theme[mode];

  return {
    // Equal to antd's default in light; the dark elevated surface is not `surface.main.cards`.
    contentBg: surface.main.cards,
    // Same token as the content: see the note above on darkAlgorithm's colorBgElevated.
    headerBg: surface.main.cards,
    titleFontSize: toPx(tokens.typography.fontSize.heading.h3),
    // antd wants a unitless ratio and the ramp already carries one: 1.3, i.e. Figma's 26/20.
    titleLineHeight: tokens.typography.lineHeight.heading.h3,
    borderRadiusLG: toPx(tokens.spacing.radius['300']),
    boxShadow: tokens.shadows.modal,
    colorIcon: icon.primaryStandalone,
    // camelCase in the JS tokens; the CSS var is --icon-primary-hover.
    colorIconHover: icon.primaryHover
  };
};

/**
 * Select declares a ComponentToken of only `{ zIndexPopup }` in 5.6.4, so every key below is an *alias*
 * token scoped to the component name, and a key outside the set `select/style` actually reads is silently
 * inert — `optionSelectedBg` is one such: it does not exist in 5.6.4, so `controlItemBgActive` carries the
 * selected-option background instead.
 *
 * The two values the generated bridge gets wrong are corrected here: `controlHeight` is out of date, and
 * `colorPrimaryHover` is a dark olive rather than the focus border.
 */
const selectComponent = (mode: 'light' | 'dark') => {
  const { surface, text, border, icon } = tokens.theme[mode];
  const { radius } = tokens.spacing;

  return {
    controlHeight: 32,
    // The generated bridge set this equal to controlHeight, which made `size="small"` a no-op.
    controlHeightSM: 28,
    borderRadius: toPx(radius[200]),
    // The option row and the multi-select tag; antd defaults both to 4.
    borderRadiusSM: toPx(radius[200]),
    // The panel, shared with the TreeSelect popup.
    borderRadiusLG: toPx(radius[200]),

    // Trigger.
    colorBgContainer: surface.main.cards,
    colorBgContainerDisabled: surface.main.cards,
    colorBorder: border.inputFieldDefault2,
    colorPrimaryHover: border.focusPrimary,
    // Since antd 5.13 the focused border reads `activeBorderColor` (default colorPrimary, the checkmark
    // lime below) instead of colorPrimaryHover; pinned so focus keeps the same border as hover.
    activeBorderColor: border.focusPrimary,
    // The same ring Input uses. The bridge's `surface.primary.background` (#fafeec) was invisible on a
    // white card, so a focused Select read as unfocused (UI-polish review, item B).
    controlOutline: border.focusPrimary,
    colorText: text.heading,
    colorTextPlaceholder: text.secondary,
    colorTextDisabled: text.disabled,
    // The suffix icon and the allowClear cross, not body text.
    colorTextQuaternary: icon.secondary,
    colorTextTertiary: icon.primaryStandalone,
    // The multi-select tag background, and its remove cross resting and hovered.
    colorFillSecondary: surface.main['element-on-card'],
    colorIcon: icon.secondary,
    colorIconHover: icon.primaryStandalone,

    // Panel.
    colorBgElevated: surface.main.cards,
    controlItemBgHover: surface.main['element-on-card'],
    controlItemBgActive: surface.secondary.background,
    colorTextDescription: text.secondary,
    // The checkmark on the selected option.
    colorPrimary: surface.primary.action
  };
};

/* Tag has no entry in the generated bridge, so every chip not routed through `components/common/tag`
   took antd's stock geometry — a ~10px unstyled close cross beside 12px text (item E). Geometry only;
   colours stay with the tag component tokens. */
const tagComponent = () => ({
  borderRadiusSM: toPx(tokens.spacing.radius['100']),
  fontSizeSM: 12,
  fontSizeIcon: 12
});

/**
 * The generated bridge ships antd's own `rgba(0, 0, 0, 0.65)` here, which is not a DS value. One
 * backdrop token exists and it is intentionally identical in both modes, because the scrim is one
 * colour over both a light and a dark page.
 *
 * Global rather than scoped under `components.Modal`, matching where the generated bridge puts it:
 * Drawer and Image preview read the same key, and there is no reason for three different scrims. That
 * does mean this moves the drawer's mask too - covered by APO-462's Chromatic review.
 */
const scrimToken = ({ mode }: Env0Theme) => ({
  colorBgMask: tokens.theme[mode].surface.main['modal-backdrop']
});

/* One focus ring for every antd consumer of `genFocusStyle`. Unpinned, antd derives
   `colorPrimaryBorder` from the lime seed — a 4px halo no DS token specifies, which outranked the
   design system's own ring. Pinned to the pair the text fields already use (item B). */
const focusRingTokens = (mode: Env0Theme['mode']) => ({
  lineWidthFocus: toPx(tokens.spacing.borderWidth.sm),
  colorPrimaryBorder: tokens.theme[mode].border.focusPrimary
});

/* The bridge ships colorLink as near-black with a white hover, so antd's global `a` rule painted body
   links as plain text and flipped card links to black on hover. One value for all three states; the
   hover underline lives in `components/common/link.tsx`. Token gap raised with design. */
const linkTokens = (mode: Env0Theme['mode']) => ({
  colorLink: tokens.theme[mode].text.tint.lime,
  colorLinkHover: tokens.theme[mode].text.tint.lime,
  colorLinkActive: tokens.theme[mode].text.tint.lime
});

/**
 * `genTreeStyle` derives the row height from `controlHeightSM` and the row padding from `paddingXS / 2` —
 * under Select the same `controlHeightSM` is the small trigger height instead, so the two sections read one
 * name as two different measurements.
 *
 * Set under both names on purpose: `genComponentStyleHook` scopes overrides by the literal name the hook
 * registered with, and `tree-select/style` registers as `TreeSelect` while generating its rows with Tree's
 * `genTreeStyle`, so a TreeSelect popup never sees `components.Tree`. Both hooks also run `checkbox/style`
 * under their own scope, which is why `colorPrimary` here is what paints tree checkboxes.
 */
const treeRowTokens = (mode: 'light' | 'dark') => {
  const { surface, text, border } = tokens.theme[mode];

  return {
    controlHeightSM: 32,
    paddingXS: toPx(tokens.spacing.padding.xs),
    borderRadius: toPx(tokens.spacing.radius[200]),
    colorBgContainer: surface.main.cards,
    controlItemBgHover: surface.main['element-on-card'],
    controlItemBgActive: surface.secondary.background,
    colorPrimary: surface.primary.action,
    colorTextDisabled: text.disabled,
    // The connector lines under `showLine`.
    colorBorder: border.primary
  };
};

// Dropdown ships its own menu styles and reads none of `components.Menu`, so this is the only token path
// to a dropdown item.

const dropdownComponent = (mode: 'light' | 'dark') => {
  const { surface, text } = tokens.theme[mode];

  return {
    colorBgElevated: surface.main.cards,
    controlItemBgHover: surface.main['element-on-card'],
    controlItemBgActive: surface.secondary.background,
    controlItemBgActiveHover: surface.secondary['action-hover'],
    colorPrimary: text.heading,
    colorText: text.heading,
    colorTextDescription: text.disabled,
    colorSplit: surface.main['element-on-card'],
    borderRadiusLG: toPx(tokens.spacing.radius[200]),
    borderRadiusSM: toPx(tokens.spacing.radius[200])
  };
};

/**
 * https://www.figma.com/design/vhfYlbrrUsQrx6AvWGcE9h/Design-system?node-id=152-34
 *
 * Measured in the live file, not read off the component's description, which says "Circle 32" where 32 is
 * the radius and "label 16" where the bound variable is 14.
 *
 * Both filled states use one token for fill *and* border, which is what lets the bridge carry them: antd
 * derives process from `colorPrimary` and finish from `controlItemBgActive`, each painting both. The four
 * things it cannot derive live in `components/common/bridged-steps.tsx`.
 *
 * `colorTextLightSolid` is `text.onAction`, not `icon.primary`: the Current fill is the same lime in both
 * modes while `icon.primary` flips to white in dark, which would put white on lime at 1.4:1.
 *
 * Three keys are absent on purpose: `iconSizeSM` already equals 24 in 5.6.4, `padding` - the label's
 * inline-end inset, which is Figma's 16px step gap - already equals 16, and `fontSize` is already 14
 * globally. Setting any of them would be noise.
 */
const stepsComponent = (mode: Mode) => {
  const { text, border, icon, surface } = tokens.theme[mode];

  return {
    iconSize: 24,
    iconFontSize: 16,
    titleLineHeight: 24,
    // The title reads `fontSizeLG`, not `fontSize`; without this the label stays on antd's 16.
    fontSizeLG: 14,
    // The 12px gap between circle and label, which the icon's margin-inline-end derives from.
    marginXS: toPx(tokens.spacing.spacing.md),
    // The current step's title, antd's one `fontWeightStrong` read within Steps. Figma measures Regular;
    // 500 renders Regular under the ABC Whyte collapse (400 -> Light). States are distinguished by colour.
    fontWeightStrong: 500,

    // Current: fill and border both the lime.
    colorPrimary: surface.primary.action,
    colorTextLightSolid: text.onAction,
    // Finish: fill and border both the tint.
    controlItemBgActive: surface.primary.background,
    // Upcoming has no fill in the design; its ring is in the wrapper.
    colorFillContent: 'transparent',

    colorText: text.heading,
    // Upcoming's label and numeral. `text.disabled` is what Figma binds, but in light it is #808080 on the
    // card, 3.95:1, under AA for 14px text. In dark the two tokens are the same neutral-600, so this is a
    // light-mode correction only. Deviation raised with design (APO-471).
    colorTextDescription: text.secondary,
    colorTextLabel: text.secondary,
    // Only reaches `waitDotColor`, an upcoming step's own `icon`, within this component's scope. Same
    // choice as the labels above, so a custom icon and its label match.
    colorTextDisabled: text.secondary,
    colorSplit: border.primary,
    navArrowColor: icon.secondary
  };
};

/**
 * antd wants numbers; the theme carries px strings so emotion can interpolate them directly.
 *
 * All 17 keys are set, not just the 6 bases: antd 5.6.4 does not re-derive the *Min/*Max aliases when
 * a base is overridden, so moving screenMD to 900 while screenSMMax stays 767 leaves 768-899 uncovered.
 */
const toScreenTokens = ({ breakpoints }: Env0Theme) => {
  const px = (step: keyof Breakpoints) => parseInt(breakpoints[step], 10);
  const xs = px('xs');
  const sm = px('sm');
  const md = px('md');
  const lg = px('lg');
  const xl = px('xl');
  const xxl = px('xxl');

  return {
    screenXS: xs,
    screenXSMin: xs,
    screenXSMax: sm - 1,
    screenSM: sm,
    screenSMMin: sm,
    screenSMMax: md - 1,
    screenMD: md,
    screenMDMin: md,
    screenMDMax: lg - 1,
    screenLG: lg,
    screenLGMin: lg,
    screenLGMax: xl - 1,
    screenXL: xl,
    screenXLMin: xl,
    screenXLMax: xxl - 1,
    screenXXL: xxl,
    screenXXLMin: xxl
  };
};

export const getAntdTheme = (theme: Env0Theme): ThemeConfig => {
  // getGeneratedAntdTheme returns a module-level object, so every addition has to be a copy.
  const generated = getGeneratedAntdTheme(theme.mode);

  return {
    ...generated,
    token: {
      ...generated.token,
      fontFamily: `'${PRIMARY_FONT_FAMILY}', sans-serif`,
      ...zIndexTokens,
      ...toScreenTokens(theme),
      ...scrimToken(theme),
      ...focusRingTokens(theme.mode),
      ...linkTokens(theme.mode)
    },
    // InputNumber and DatePicker take the same block: InputNumber's own ComponentToken in 5.6.4 is
    // controlWidth, handleWidth, handleFontSize and handleVisible, none of which the design specifies,
    // and DatePicker otherwise keeps the bridge's mis-bound Success-green hover and a derived ring no
    // other field shows.
    components: merge(
      {},
      generated.components,
      componentOverrides(theme),
      feedbackComponents(theme.mode),
      avatarAndBadgeComponents(theme.mode),
      {
        Alert: statusTokens(theme.mode),
        Button: buttonComponent(theme.mode),
        Input: fieldComponent(theme.mode),
        InputNumber: fieldComponent(theme.mode),
        DatePicker: fieldComponent(theme.mode),
        Menu: menuComponent(theme.mode),
        Modal: modalComponent(theme.mode),
        Pagination: paginationComponent(theme.mode),
        Table: tableComponent(theme.mode),
        Tabs: tabsComponent(theme.mode),
        Switch: switchComponent(theme.mode),
        Card: cardComponent(theme.mode),
        Collapse: collapseComponent(theme.mode),
        Checkbox: checkboxComponent(theme.mode),
        Radio: radioComponent(theme.mode),
        Select: selectComponent(theme.mode),
        Steps: stepsComponent(theme.mode),
        Tree: treeRowTokens(theme.mode),
        TreeSelect: treeRowTokens(theme.mode),
        Dropdown: dropdownComponent(theme.mode),
        Tag: tagComponent()
      }
    )
  };
};
